import { PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  FeedbackQueryParams,
  FeedbackResponse,
  FeedbackStatusUpdatePayload,
  FeedbackRoutePayload,
  FeedbackJsmRoutingConfigItem,
  UpdateFeedbackJsmRoutingConfigPayload,
} from '@/types/feedback.types';

export const UserFeedbackService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFeedbacks: builder.query<
      PaginatedApiResponse<FeedbackResponse>,
      FeedbackQueryParams
    >({
      query: (params) => ({
        url: 'feedback',
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<FeedbackResponse>) =>
        response,
      providesTags: ['Feedback'],
    }),
    getFeedbackById: builder.query<FeedbackResponse, string>({
      query: (id) => ({
        url: `feedback/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Feedback', id }],
    }),
    getFeedbackJsmRoutingConfig: builder.query<
      FeedbackJsmRoutingConfigItem[],
      void
    >({
      query: () => ({
        url: 'feedback/jsm-routing-config',
        method: 'GET',
      }),
      providesTags: ['Feedback'],
    }),
    updateFeedbackJsmRoutingConfig: builder.mutation<
      FeedbackJsmRoutingConfigItem[],
      UpdateFeedbackJsmRoutingConfigPayload
    >({
      query: (payload) => ({
        url: 'feedback/jsm-routing-config',
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['Feedback'],
    }),

    exportFeedbackCsv: builder.query<{ fileUrl: string }, FeedbackQueryParams>({
      query: (params) => ({
        url: '/feedback/export/csv',
        method: 'GET',
        params,
      }),
    }),
    updateFeedbackStatus: builder.mutation<
      FeedbackResponse,
      { id: string; payload: FeedbackStatusUpdatePayload }
    >({
      query: ({ id, payload }) => ({
        url: `feedback/${id}`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: ['Feedback'],
    }),
    routeFeedback: builder.mutation<
      FeedbackResponse,
      { id: string; payload: FeedbackRoutePayload }
    >({
      query: ({ id, payload }) => ({
        url: `feedback/${id}/route`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: ['Feedback'],
    }),
  }),
});

export const {
  useGetFeedbacksQuery,
  useGetFeedbackByIdQuery,
  useGetFeedbackJsmRoutingConfigQuery,
  useUpdateFeedbackJsmRoutingConfigMutation,
  useExportFeedbackCsvQuery,
  useUpdateFeedbackStatusMutation,
  useRouteFeedbackMutation,
} = UserFeedbackService;
